import React, { useCallback, useEffect, useState } from 'react';
import { resolveAnyStaticUrl } from '../../urlConfig';
import { listManualReviewCandidates, queueManualReviewProducts } from './catalogReviewApi';

const errorText = (error) => error?.response?.data?.message || error?.message || 'Ошибка загрузки';

const CatalogManualReviewQueueModal = ({ categoryId, onClose, onQueued }) => {
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(0);
    const [data, setData] = useState({ rows: [], pagination: {} });
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setPage(0);
            setQuery(search.trim());
        }, 300);
        return () => clearTimeout(timeout);
    }, [search]);

    const load = useCallback(async (isMounted = () => true) => {
        setLoading(true);
        try {
            const result = await listManualReviewCandidates({ categoryId, search: query, page, limit: 20 });
            if (isMounted()) setData(result);
        } catch (error) {
            if (isMounted()) setMessage({ type: 'error', text: errorText(error) });
        } finally {
            if (isMounted()) setLoading(false);
        }
    }, [categoryId, page, query]);

    useEffect(() => {
        let live = true;
        load(() => live);
        return () => { live = false; };
    }, [load]);

    const rows = data.rows || [];
    const usable = (product) => !product?.review;
    const toggle = (id) => setSelected((previous) => (
        previous.includes(id) ? previous.filter((value) => value !== id) : [...previous, id]
    ));
    const togglePage = () => {
        const available = rows.filter(usable).map((row) => String(row._id));
        setSelected((previous) => available.every((id) => previous.includes(id))
            ? previous.filter((id) => !available.includes(id))
            : Array.from(new Set([...previous, ...available])).slice(0, 50));
    };

    const submit = async () => {
        if (!selected.length || submitting) return;
        setSubmitting(true);
        setMessage(null);
        try {
            const result = await queueManualReviewProducts(selected, categoryId);
            const added = Number(result.addedCount || 0);
            const failures = (result.results || []).filter((row) => row.status !== 'added');
            setMessage({
                type: failures.length ? 'warning' : 'success',
                text: `Отправлено на проверку: ${added}. ${failures.length ? `Уже привязаны или отклонены: ${failures.length}.` : ''}`,
            });
            setSelected([]);
            await load();
            if (added) onQueued?.(result);
        } catch (error) {
            setMessage({ type: 'error', text: errorText(error) });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="catalogReview__manualQueueBackdrop" role="presentation" onMouseDown={() => { if (!submitting) onClose(); }}>
            <section className="catalogReview__manualQueueModal" role="dialog" aria-modal="true"
                aria-label="Ручная отправка памятников на проверку цены"
                onMouseDown={(event) => event.stopPropagation()}>
                <header>
                    <div>
                        <strong>Отправить памятники на проверку цен</strong>
                        <small>Выбирай существующие товары с 3D. Названия и номера изменять не нужно.</small>
                    </div>
                    <button type="button" onClick={onClose} disabled={submitting} aria-label="Закрыть">✕</button>
                </header>
                <div className="catalogReview__manualQueueToolbar">
                    <input value={search} onChange={(event) => setSearch(event.target.value)}
                        placeholder="Поиск по названию, артикулу, описанию" aria-label="Найти товар" />
                    <button type="button" onClick={togglePage} disabled={loading || !rows.some(usable)}>
                        Выбрать доступные на странице
                    </button>
                </div>
                {message ? (
                    <div className={`catalogReview__message catalogReview__message--${message.type}`}>{message.text}</div>
                ) : null}
                <div className="catalogReview__manualQueueList">
                    {loading ? <p>Загружаем товары…</p> : null}
                    {!loading && !rows.length ? <p>Товары с 3D не найдены.</p> : null}
                    {!loading && rows.map((product) => {
                        const id = String(product._id);
                        const existing = product.review;
                        return (
                            <label key={id} className={`catalogReview__manualQueueItem ${existing ? 'is-linked' : ''}`}>
                                <input type="checkbox" disabled={Boolean(existing) || submitting || (selected.length >= 50 && !selected.includes(id))}
                                    checked={selected.includes(id)} onChange={() => toggle(id)} />
                                {product.reducedImage ? <img src={resolveAnyStaticUrl(product.reducedImage)} alt="" loading="lazy" /> : <span className="catalogReview__manualQueueNoImage">3D</span>}
                                <span className="catalogReview__manualQueueItemText">
                                    <strong>{product.fullName || product.name || 'Без названия'}</strong>
                                    <small>Артикул: {product.article || '—'} · {product.description || 'Без номера'}</small>
                                    {existing ? <small className="catalogReview__manualQueueLinked">Уже в проверке · {existing.reviewStatus === 'unchecked' ? 'Не проверен' : 'Запись существует'}</small> : <small>3D есть · можно отправить на проверку</small>}
                                </span>
                            </label>
                        );
                    })}
                </div>
                <footer>
                    <span>Выбрано: {selected.length} · Всего найдено: {Number(data.pagination?.total || 0)}</span>
                    <div className="catalogReview__manualQueuePagination">
                        <button type="button" disabled={page === 0 || loading || submitting} onClick={() => setPage((x) => x - 1)}>Назад</button>
                        <span>{page + 1} / {Math.max(1, data.pagination?.totalPages || 1)}</span>
                        <button type="button" disabled={loading || submitting || page + 1 >= (data.pagination?.totalPages || 1)} onClick={() => setPage((x) => x + 1)}>Далее</button>
                    </div>
                    <button type="button" className="is-primary" disabled={!selected.length || submitting}
                        onClick={submit}>{submitting ? 'Отправляем…' : `Отправить на проверку · ${selected.length}`}</button>
                </footer>
            </section>
        </div>
    );
};

export default CatalogManualReviewQueueModal;
